// @flow
import useFirebase from './firebase';
import { useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchPods, selectPod, fetchPodBanners, fetchAffilate, setAffilateData } from '../actions/pods';
import { setCookie, deleteCookie } from '../utils';
import { showErrorMsg, showSuccessMsg } from '../actions/snackbar';
import { updateSessionData } from '../actions/auth';
import { logEvent as logEventAction } from '../actions/log';
import { AFFILIATE_ACTIVE, AFFILIATE_DATA } from '../constants';

const usePods = () => {
	const podData = useSelector((state) => state.pod);
	const { affiliateActive, affiliateData, selectedPod } = podData;
	const { mobile } = useSelector((state) => state.user || {});
	const { sessionId } = useSelector((state) => state.auth || {});
	const dispatch = useDispatch();
	const [logEvent, setUserProperties] = useFirebase();

	const changeSelectedPod = useCallback(
		(data: { [key: string]: string }) => {
			dispatch(selectPod(data));
			logEvent('select_location', { location: data.location });
			setUserProperties({ location: data.location });
			dispatch(updateSessionData({ machine: data }, sessionId));
		},
		[dispatch, logEvent, sessionId, setUserProperties]
	);

	const resetSelectedPod = useCallback(() => {
		dispatch(selectPod());
	}, [dispatch]);

	const showErrorMsgCb = useCallback(
		(msg: string, timeout: number = 2000) => {
			dispatch(showErrorMsg(msg, timeout));
		},
		[dispatch]
	);

	const fetchPodsCb = useCallback(() => {
		dispatch(fetchPods(mobile));
	}, [dispatch, mobile]);

	const fetchPodBannersCb = useCallback(
		(imei: string) => {
			dispatch(fetchPodBanners(imei));
		},
		[dispatch]
	);

	const suggestMachineCb = useCallback(
		(eventName: string, name: string, mobile: string, address: string, location: string, imei: string) => {
			logEvent(eventName, {
				name,
				mobile,
				address,
				location,
			});
			dispatch(showSuccessMsg('Thank you for your Suggestion!'));
			dispatch(
				logEventAction({
					user_id: mobile,
					imei: imei,
					event_name: eventName,
					logs: JSON.stringify({
						name,
						mobile,
						address,
					}),
				})
			);
		},
		[dispatch, logEvent]
	);

	const fetchAffilateCb = useCallback(
		(url: string, navigated: boolean) => {
			dispatch(fetchAffilate(url, navigated, mobile));
		},
		[dispatch, mobile]
	);

	const setAffilateCb = useCallback(
		(active: boolean, data?: { [key: string]: any }) => {
			dispatch(setAffilateData(active, data));
		},
		[dispatch]
	);

	useEffect(() => {
		if (!affiliateActive) {
			deleteCookie(AFFILIATE_ACTIVE);
			deleteCookie(AFFILIATE_DATA);
			if (selectedPod) {
				setUserProperties({ location: selectedPod.location });
			}
		} else {
			setCookie(AFFILIATE_ACTIVE, '1');
			if (affiliateData) {
				setCookie(AFFILIATE_DATA, JSON.stringify(affiliateData));
				const { page_id = 'affiliate' } = affiliateData;
				setUserProperties({ location: page_id });
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [affiliateActive, affiliateData]);

	useEffect(() => {
		if (selectedPod) {
			setCookie('pod', JSON.stringify(selectedPod));
		} else {
			deleteCookie('pod');
		}
	}, [selectedPod]);

	return [
		podData,
		changeSelectedPod,
		resetSelectedPod,
		showErrorMsgCb,
		fetchPodsCb,
		fetchPodBannersCb,
		suggestMachineCb,
		fetchAffilateCb,
		setAffilateCb,
	];
};

export default usePods;
